import { Injectable } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../../core/models/form-group-type';
import { ScheduleKFormModel } from '../models/schedule-k-form-model';
import { ActionCode } from '../../../../../core/models/resources/action-code';
import { ScheduleKFrequencyFormModel } from '../models/schedule-k-frequency-form-model';
import { FrequencyTypeCode } from '../../schedules-d-and-h/models/frequency-type-codes';
import { FrequencyHelper } from '../../../../../core/helpers/frequency-helper/frequency-helper';

@Injectable({
    providedIn: 'root',
})
export class ScheduleKService {
    constructor() {}

    getFormDefinition() {
        return new FormGroup<NestedFormGroupType<ScheduleKFormModel>>({
            notificationCode: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            callSign: new FormControl({ value: '', disabled: true }),
            scheduleKFrequencies: new FormArray<
                FormGroup<FormGroupType<ScheduleKFrequencyFormModel>>
            >([], [Validators.required]),
        });
    }

    getScheduleKFrequencyFormDefinition() {
        return new FormGroup<FormGroupType<ScheduleKFrequencyFormModel>>({
            locationNumber: new FormControl<number | null>(null),
            frequencyLow: new FormControl<number | null>(null),
            frequencyHigh: new FormControl<number | null>(null),
            constructionDeadlineDate: new FormControl<Date | null>(null),
            actualDateOfConstruction: new FormControl<Date | null>(null, [
                this.isDateRequiredValidator(),
                this.isDateInFutureValidator(),
            ]),
            numberOfMobiles: new FormControl<string | null>(null),
            id: new FormControl<number | null>(null),
            actionCode: new FormControl<ActionCode | null>(null),
        });
    }

    isDateInFutureValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value === null) {
                return null;
            }
            const dateValue = new Date(control.value);

            if (dateValue && dateValue > new Date()) {
                return {
                    invalid:
                        'Actual date of construction cannot be in the future',
                };
            }

            return null;
        };
    }

    isDateRequiredValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const dateValue = control.value;
            const gridControls = control?.parent as FormGroup<
                FormGroupType<ScheduleKFrequencyFormModel>
            >;
            const gridData = gridControls?.controls;
            if (!gridData) {
                return null;
            }

            // Trigger validation highlighting
            gridData.numberOfMobiles.markAsTouched();

            if (
                (dateValue === null || dateValue === '') &&
                gridData.numberOfMobiles.value !== '' &&
                gridData.numberOfMobiles.value !== null
            ) {
                return { required: 'Date is required' };
            }

            return null;
        };
    }

    isFrequencyWithinMobileRange(frequency: number | null): boolean {
        if (frequency == null) {
            return false;
        }
        return FrequencyHelper.isBetweenAnyFrequencyRanges(
            frequency,
            this.getMobileRangeFrequencyPairs(),
        );
    }

    private getMobileRangeFrequencyPairs(): FrequencyTypeCode[] {
        return [
            FrequencyTypeCode.TBand,
            FrequencyTypeCode.Between806And870MHz,
            FrequencyTypeCode.Between896And901MHz,
            FrequencyTypeCode.Between935And940MHz,
        ];
    }
}
